import {
  MagnifyingGlassIcon,
  Square2StackIcon
} from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import ResetIcon from 'components/icons/reset-icon';
import Text from 'components/inputs/text';
import GridDraggableItem from 'components/structure-page/2d/grid-draggable-item';
import { ItemType } from 'data/types';
import { addNewSpaceToStructure } from 'features/structure';
import { useNewSpaceGate } from 'hooks/use-new-space-gate';
import { useSpaceTypeGroups } from 'hooks/use-space-type-groups';
import { useState } from 'react';
import { structureEditResetsDesigns } from 'utils/structure-edit-reset';
import { confirmationModal } from 'utils/ui';

export default function SpaceList() {
  const dispatch = useAppDispatch();
  const { configurations } = useAppSelector((state) => state.configurations);
  const { selectedSystems } = useAppSelector((state) => state.systems);
  const [searching, setSearching] = useState(false);
  const [searchText, setSearchText] = useState('');
  const {
    isBlocked: isDragDisabled,
    isFloorFullyCovered,
    reason: dragDisabledTooltip
  } = useNewSpaceGate();
  const {
    suggested,
    common,
    other,
    detached,
    isAnalyzingSuggestions,
    isEmpty
  } = useSpaceTypeGroups(searchText);

  const handleAddNewSpace = async (itemId: number) => {
    await dispatch(addNewSpaceToStructure({ spaceItemId: itemId }));
  };

  const addSpace = (itemId: number) => {
    if (isFloorFullyCovered) {
      return;
    }
    if (structureEditResetsDesigns(configurations, selectedSystems)) {
      confirmationModal(
        'Are you sure you want to add a space? This will reset all configurations and selected systems!',
        () => handleAddNewSpace(itemId)
      );
    } else {
      handleAddNewSpace(itemId);
    }
  };

  const renderGroup = (title: string, items: ItemType[], keyPrefix = '') => {
    if (items.length === 0) {
      return null;
    }
    return (
      <>
        <div className='text-lg font-semibold'>{title}</div>
        {items.map((item) => (
          <GridDraggableItem
            key={`${keyPrefix}${item.id}`}
            id={`space-${item.id}`}
            label={item.label}
            type='new-space'
            itemId={item.id}
            disabled={isDragDisabled}
            tooltip={dragDisabledTooltip}
            onDoubleClick={() => addSpace(item.id)}
          />
        ))}
      </>
    );
  };

  return (
    <div className='flex w-full grow flex-col gap-2 overflow-hidden'>
      <div className='flex items-center gap-1'>
        <Square2StackIcon className='h-6 w-6 shrink-0' />
        {searching ? (
          <Text
            value={searchText}
            onInput={setSearchText}
            onBlur={() => setSearching(false)}
            autoFocus
            autoSelect
            size='sm'
            placeholder='Search...'
          />
        ) : (
          <>
            <span className='grow text-lg font-semibold'>
              Spaces{searchText.length > 0 && ' (Filtered)'}
            </span>
            {searchText.length > 0 ? (
              <ResetIcon
                className='h-7 w-7 shrink-0 cursor-pointer rounded-md p-[3px] hover:bg-gray-100'
                onClick={() => setSearchText('')}
              />
            ) : (
              <MagnifyingGlassIcon
                className='h-7 w-7 shrink-0 cursor-pointer rounded-md p-[3px] hover:bg-gray-100'
                onClick={() => setSearching(true)}
              />
            )}
          </>
        )}
      </div>
      <div className='flex grow flex-col gap-1 overflow-auto'>
        {isAnalyzingSuggestions && (
          <>
            <div className='text-lg font-semibold'>Your Spaces</div>
            <div className='flex items-center gap-2 px-1 py-1 text-sm text-gray-500'>
              <svg
                className='h-4 w-4 shrink-0 animate-spin'
                xmlns='http://www.w3.org/2000/svg'
                viewBox='0 0 512 512'
              >
                <path
                  fill='gray'
                  className='opacity-40'
                  d='M256 64C150 64 64 150 64 256s86 192 192 192c70.1 0 131.3-37.5 164.9-93.6l.1 .1c-6.9 14.9-1.5 32.8 13 41.2c15.3 8.9 34.9 3.6 43.7-11.7c.2-.3 .4-.6 .5-.9l0 0C434.1 460.1 351.1 512 256 512C114.6 512 0 397.4 0 256S114.6 0 256 0c-17.7 0-32 14.3-32 32s14.3 32 32 32z'
                />
                <path
                  fill='gray'
                  d='M224 32c0-17.7 14.3-32 32-32C397.4 0 512 114.6 512 256c0 46.6-12.5 90.4-34.3 128c-8.8 15.3-28.4 20.5-43.7 11.7s-20.5-28.4-11.7-43.7c16.3-28.2 25.7-61 25.7-96c0-106-86-192-192-192c-17.7 0-32-14.3-32-32z'
                />
              </svg>
              Analyzing image for suggested spaces...
            </div>
          </>
        )}
        {renderGroup('Your Spaces', suggested, 'suggested-')}
        {renderGroup('Common Spaces', common)}
        {renderGroup('Other Spaces', other)}
        {renderGroup('Detached Spaces', detached)}
        {isEmpty && (
          <div className='text-center text-sm text-gray-500'>
            No spaces found
          </div>
        )}
      </div>
    </div>
  );
}
